import React from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Eye, ListTree, Hash, Type, Box, Play, SkipForward, SkipBack, Square, FastForward, Sparkles, BookOpen, Loader2 } from 'lucide-react';

const getTypeIcon = (type) => {
  switch (type) {
    case 'int':
    case 'float':
      return <Hash className="h-3 w-3" />;
    case 'str':
      return <Type className="h-3 w-3" />;
    case 'list':
    case 'tuple':
      return <ListTree className="h-3 w-3" />;
    case 'dict':
    case 'set':
      return <Box className="h-3 w-3" />;
    default:
      return <Eye className="h-3 w-3" />;
  }
};

const getTypeColor = (type) => {
  const colors = {
    int: 'bg-primary/20 text-primary border-primary/30',
    float: 'bg-primary/20 text-primary border-primary/30',
    str: 'bg-secondary/20 text-secondary border-secondary/30',
    list: 'bg-accent/20 text-accent border-accent/30',
    tuple: 'bg-accent/20 text-accent border-accent/30',
    dict: 'bg-warning/20 text-warning-foreground border-warning/30',
    set: 'bg-warning/20 text-warning-foreground border-warning/30',
    bool: 'bg-success/20 text-success border-success/30',
  };
  return colors[type] || 'bg-muted text-muted-foreground border-border';
};

const VariableCard = ({ variable }) => {
  const { name, type, value } = variable;
  
  // Parse the value
  let displayValue = value;
  let isArray = false;
  let arrayItems = [];
  
  try {
    if (type === 'list' || type === 'tuple' || type === 'set') {
      // Try to parse as array
      const parsed = JSON.parse(value.replace(/'/g, '"').replace(/\(/g, '[').replace(/\)/g, ']').replace(/\{/g, '[').replace(/\}/g, ']'));
      if (Array.isArray(parsed)) {
        isArray = true;
        arrayItems = parsed;
      }
    } else if (type === 'bool') {
      displayValue = value === 'True' ? '✓ True' : '✗ False';
    } else if (type === 'str') {
      // Remove quotes for display
      displayValue = value.replace(/^["']|["']$/g, '');
    }
  } catch (e) {
    // Keep original value
  }

  return (
    <div className={`rounded-xl border-2 p-3 ${getTypeColor(type)} transition-all duration-300 hover:scale-[1.02] card-hover`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getTypeIcon(type)}
          <span className="font-bold font-mono text-sm">{name}</span>
        </div>
        <Badge variant="outline" className="text-[10px] h-5 px-1.5">
          {type}
        </Badge>
      </div>
      
      <div className="font-mono text-sm" dir="ltr">
        {isArray ? (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {arrayItems.slice(0, 8).map((item, idx) => (
              <div 
                key={idx}
                className="bg-background/50 rounded-lg px-2.5 py-1.5 border border-border/30 text-xs font-medium shadow-sm"
              >
                {String(item)}
              </div>
            ))}
            {arrayItems.length > 8 && (
              <div className="bg-background/50 rounded-lg px-2.5 py-1.5 border border-border/30 text-xs font-medium shadow-sm opacity-60">
                +{arrayItems.length - 8} עוד
              </div>
            )}
          </div>
        ) : (
          <div className={`text-lg font-bold mt-1 ${type === 'bool' && value === 'True' ? 'text-success' : ''}`}>
            {displayValue}
          </div>
        )}
      </div>
    </div>
  );
};

// Simple markdown-ish renderer for full explanations (RTL-aware)
const SimpleMarkdown = ({ text }) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  
  return (
    <div dir="rtl" className="space-y-2 text-sm text-foreground leading-relaxed text-right">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        
        if (!trimmed) return <div key={idx} className="h-1" />;
        
        // Headers
        if (trimmed.startsWith('### ')) {
          return <h4 key={idx} className="font-bold text-sm mt-3 mb-1 text-right">{trimmed.slice(4)}</h4>;
        }
        if (trimmed.startsWith('## ')) {
          return <h3 key={idx} className="font-bold text-base mt-3 mb-1 text-right">{trimmed.slice(3)}</h3>;
        }
        if (trimmed.startsWith('# ')) {
          return <h2 key={idx} className="font-bold text-lg mt-3 mb-1 text-right">{trimmed.slice(2)}</h2>;
        }
        
        // Code blocks
        if (trimmed.startsWith('```')) {
          return null; // Skip code fence lines
        }
        
        // Helper to render inline markdown (bold + code)
        const renderInline = (raw) => {
          const withCode = raw.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-xs font-mono" dir="ltr">$1</code>');
          return withCode.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        };

        // Numbered lists: in RTL show number on the right (number first in DOM = right side)
        const numListMatch = trimmed.match(/^(\d+)\.\s(.+)$/);
        if (numListMatch) {
          const [, num, content] = numListMatch;
          return (
            <p key={idx} className="text-right ps-6">
              <span className="font-medium me-2" aria-hidden>{num}.</span>
              <span dangerouslySetInnerHTML={{ __html: renderInline(content) }} />
            </p>
          );
        }
        
        // Bullet lists: in RTL bullet on the right
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          const bulletContent = trimmed.slice(2);
          return (
            <p key={idx} className="text-right ps-6">
              <span className="me-2" aria-hidden>•</span>
              <span dangerouslySetInnerHTML={{ __html: renderInline(bulletContent) }} />
            </p>
          );
        }
        
        // Regular paragraph with inline markdown
        return <p key={idx} className="text-right" dangerouslySetInnerHTML={{ __html: renderInline(trimmed) }} />;
      })}
    </div>
  );
};

export const VisualizerPane = ({ 
  isDebugging = false,
  isPreparingDebug = false,
  currentLine = 0,
  variables = [],
  explanations = {},
  fullExplanation = null,
  explanationLoading = false,
  debugSteps = [],
  currentStepIdx = -1,
  onStartDebug,
  onExplainCode,
  onStepForward,
  onStepBackward,
  onStopDebug,
  onRunToEnd,
}) => {
  const hasRealData = variables.length > 0 || isDebugging;
  const currentExplanation = explanations[currentLine];

  return (
    <div className="flex flex-col h-full bg-card rounded-xl overflow-hidden border border-border/30 shadow-md">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-l from-accent/10 to-primary/10 border-b border-border/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center shadow-glow-accent">
            <Eye className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">מבט פנימי</h3>
            <p className="text-xs text-muted-foreground">Inside View - דיבוג וויזואלי</p>
          </div>
        </div>
        
        {currentLine > 0 && (
          <Badge variant="secondary" className="font-mono">
            שורה {currentLine}
          </Badge>
        )}
      </div>

      {/* Debug Controls */}
      <div className="px-4 py-3 border-b border-border/20 bg-muted/30">
        <div className="flex items-center gap-2 flex-wrap">
          {!isDebugging ? (
            <>
              <Button
                onClick={onStartDebug}
                size="sm"
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                disabled={isPreparingDebug}
              >
                <Play className="h-4 w-4 ml-1" />
                {isPreparingDebug ? 'מכין דיבוג...' : 'התחל דיבוג'}
              </Button>
              <Button
                onClick={onExplainCode}
                size="sm"
                variant="outline"
                disabled={explanationLoading}
              >
                {explanationLoading ? (
                  <Loader2 className="h-4 w-4 ml-1 animate-spin" />
                ) : (
                  <BookOpen className="h-4 w-4 ml-1" />
                )}
                {explanationLoading ? 'טוען הסבר...' : 'הסבר הקוד'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={onStepBackward}
                size="sm"
                variant="outline"
                disabled={currentStepIdx <= 0}
              >
                <SkipBack className="h-4 w-4 ml-1" />
                הקודם
              </Button>
              <Button
                onClick={onStepForward}
                size="sm"
                className="bg-primary hover:bg-primary/90"
                disabled={currentStepIdx >= debugSteps.length - 1}
              >
                <SkipForward className="h-4 w-4 ml-1" />
                הבא
              </Button>
              <Button
                onClick={onRunToEnd}
                size="sm"
                variant="outline"
              >
                <FastForward className="h-4 w-4 ml-1" />
                עד הסוף
              </Button>
              <Button
                onClick={onStopDebug}
                size="sm"
                variant="destructive"
              >
                <Square className="h-4 w-4 ml-1" />
                עצור
              </Button>
            </>
          )}
        </div>
        
        {isDebugging && debugSteps.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            צעד {currentStepIdx + 1} מתוך {debugSteps.length}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 p-4">
        {/* Debug Line Explanation - shown during debugging */}
        {isDebugging && currentLine > 0 && (
          <Card className="mb-4 border-accent/40 bg-accent/5 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent" />
                שורה {currentLine}
              </CardTitle>
            </CardHeader>
            <CardContent dir="rtl" className="text-right">
              {currentExplanation ? (
                <p className="text-sm text-foreground text-right">{currentExplanation}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {Object.keys(explanations).length === 0
                    ? 'טוען הסברים...'
                    : 'מבצע שורה זו'}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Full Code Explanation - shown when not debugging */}
        {!isDebugging && fullExplanation && (
          <Card className="mb-4 border-primary/30 bg-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-primary" />
                הסבר הקוד
              </CardTitle>
            </CardHeader>
            <CardContent dir="rtl" className="text-right">
              <SimpleMarkdown text={fullExplanation} />
            </CardContent>
          </Card>
        )}

        {/* Loading state for explanation */}
        {!isDebugging && explanationLoading && !fullExplanation && (
          <Card className="mb-4 border-border/30 bg-muted/30">
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p className="text-sm">מנתח את הקוד...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Variables */}
        <div className="space-y-3">
          <h4 className="font-bold text-sm flex items-center gap-2 mb-3">
            <Box className="h-4 w-4 text-accent" />
            משתנים פעילים
            <span className="text-xs text-muted-foreground font-normal">(Active Variables)</span>
            {variables.length > 0 && (
              <Badge variant="secondary" className="text-[10px] mr-auto">
                {variables.length}
              </Badge>
            )}
          </h4>
          
          {variables.length > 0 ? (
            variables.map((variable, idx) => (
              <div 
                key={`${variable.name}-${idx}`}
                className="animate-slide-up"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <VariableCard variable={variable} />
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Eye className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">
                {isDebugging ? 'נווט בצעדים לראות משתנים' : 'אין משתנים עדיין'}
              </p>
              <p className="text-xs mt-1">
                {isDebugging ? 'כל צעד מראה את מצב המשתנים באותו רגע' : 'התחל דיבוג או הסבר קוד'}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer hint */}
      <div className="px-4 py-2 bg-muted/30 border-t border-border/20">
        <p className="text-xs text-muted-foreground text-center">
          {isDebugging 
            ? '💡 השתמש ב"הבא" ו"הקודם" לנווט בין הצעדים'
            : hasRealData 
              ? '💡 משתנים אמיתיים מהקוד שלך'
              : '💡 לחץ "התחל דיבוג" לצעדים או "הסבר הקוד" להסבר מלא'
          }
        </p>
      </div>
    </div>
  );
};
